import React, {
  useEffect,
  useState,
  useMemo,
} from 'react';
import {
  Divider,
} from '@mui/material';
import { observer } from 'mobx-react';
import { compose } from 'recompose';
import PropTypes from 'prop-types';

import Product from '../../../../../../../Model/Product';
import { InfoTag } from '../../../../../../../components/Tags';
import Accordion from '../../../../../../../components/Accordion';
import useComponentMounted from '../../../../../../../hooks/useComponentMounted';
import InfoItem from '../../../InfoItem';
import {
  InfoContainer,
  InfoWrapper,
  StyledTitle,
  StyledAccordion,
  StyledArrowIcon,
} from '../../styles';
import { accordionId } from '../../utils';

import { FeaturesContainer } from './styles';
import texts from './texts';

const EMPTY_CELL = '-';

const ProductDetailsSection = ({
  productIds,
  handleExpand,
  expanded,
  isQAP,
}) => {
  const [productDocsById, setProductDocsById] = useState({});
  const isComponentMountedRef = useComponentMounted();

  useEffect(() => {
    const init = async () => {
      const missing = productIds.filter((productId) => !productDocsById[productId]);
      if (!missing.length) {
        return;
      }
      const loaded = await Promise.all(missing.map(async (productId) => [productId, await Product.getById(productId)]));
      if (isComponentMountedRef.current) {
        setProductDocsById((previous) => ({
          ...previous,
          ...Object.fromEntries(loaded.filter(([, productDoc]) => !!productDoc)),
        }));
      }
    };
    init();
    // `productDocsById` is read to skip what is already loaded, not to trigger a reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productIds, isComponentMountedRef]);

  const products = useMemo(() => productIds.map((productId) => {
    const productDoc = productDocsById[productId];
    const featuresConfig = productDoc?.getFeaturesConfig();
    return {
      productId,
      name: productDoc?.name,
      features: (featuresConfig?.enabled || []).map((featureId) => texts[featureId] || featureId),
    };
  }), [productIds, productDocsById]);

  const renderProductInfo = ({ name, features }) => (
    <InfoWrapper>
      <InfoItem label={texts.productName}>
        {name || EMPTY_CELL}
      </InfoItem>
      <InfoItem label={texts.enabledFeatures}>
        {features.length > 0 ? (
          <FeaturesContainer>
            {features.map((feature) => (
              <InfoTag key={feature}>{feature}</InfoTag>
            ))}
          </FeaturesContainer>
        ) : (
          texts.noFeaturesEnabled
        )}
      </InfoItem>
    </InfoWrapper>
  );

  return (
    <>
      <Divider flexItem />
      <StyledAccordion
        key={texts.productDetails}
        title={<StyledTitle>{texts.productDetails}</StyledTitle>}
        expanded={(expanded === accordionId.PRODUCT_DETAILS_ACCORDION) || !isQAP}
        onChange={handleExpand(accordionId.PRODUCT_DETAILS_ACCORDION)}
        expandIcon={isQAP && <StyledArrowIcon />}
        preventRotation={!isQAP}
        disableGutters
      >
        <InfoContainer>
          {products.map((product) => (
            <Accordion
              key={product.productId}
              title={product.name || EMPTY_CELL}
              defaultExpanded={products.length === 1}
            >
              {renderProductInfo(product)}
            </Accordion>
          ))}
        </InfoContainer>
      </StyledAccordion>
    </>
  );
};

ProductDetailsSection.propTypes = {
  productIds: PropTypes.arrayOf(PropTypes.string),
  handleExpand: PropTypes.func.isRequired,
  expanded: PropTypes.string.isRequired,
  isQAP: PropTypes.bool.isRequired,
};

ProductDetailsSection.defaultProps = {
  productIds: [],
};

export default compose(
  observer,
)(ProductDetailsSection);
